// src/beta/pages/StoryReader.tsx
import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getStory } from '@/beta/services/stories';
import { useReaderStore } from '@/beta/store/reader';
import { useStoryReaderKeys } from '@/beta/hooks/useStoryReaderKeys';
import { Slide } from '@/beta/components/Slide';
import { PaginationTicks } from '@/beta/components/PaginationTicks';

/** Modal-route StoryReader (spec § 6.6).
 *  - Loads /v1/stories/[storyId] via React Query.
 *  - Tap left-edge / right-edge tap zones advance prev / next.
 *  - ESC + ArrowLeft/ArrowRight via useStoryReaderKeys.
 *  - Pagination ticks bottom-center.
 *  - Cross-dissolve animation on each slide via key={slide.id}. */
export function StoryReader() {
  const { storyId = '' } = useParams<{ storyId: string }>();
  const navigate = useNavigate();

  const slideIndex = useReaderStore((s) => s.slideIndex);
  const open = useReaderStore((s) => s.open);
  const close = useReaderStore((s) => s.close);
  const next = useReaderStore((s) => s.next);
  const prev = useReaderStore((s) => s.prev);

  // Reset reader state on mount; clean up on unmount.
  useEffect(() => {
    if (storyId) open(storyId);
    return () => close();
  }, [storyId, open, close]);

  const storyQ = useQuery({
    queryKey: ['beta', 'story', storyId],
    queryFn: () => getStory(storyId),
    enabled: !!storyId,
    staleTime: 5 * 60 * 1000,
  });

  const slides = useMemo(() => storyQ.data?.slides ?? [], [storyQ.data]);
  const total = slides.length;
  const goNext = () => next(total);
  const goPrev = () => prev();
  const onClose = () => navigate(-1);

  useStoryReaderKeys({ onNext: goNext, onPrev: goPrev, onClose });

  if (storyQ.isLoading) {
    return (
      <div
        data-testid="reader-loading"
        style={{
          position: 'fixed', inset: 0, background: '#000', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--vm-stone)', fontFamily: 'var(--vm-font-heading)',
          letterSpacing: '0.4em', textTransform: 'uppercase', fontSize: 11,
        }}
      >
        Loading story…
      </div>
    );
  }

  if (storyQ.isError || total === 0) {
    return (
      <div
        style={{
          position: 'fixed', inset: 0, background: '#000', zIndex: 60,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 'var(--vm-s-3)', color: 'var(--vm-warm-white)',
        }}
      >
        <p
          style={{
            fontFamily: 'var(--vm-font-heading)', fontSize: 12,
            letterSpacing: '0.3em', textTransform: 'uppercase',
            color: 'var(--vm-stone)',
          }}
        >
          Story unavailable
        </p>
        <button
          onClick={onClose}
          style={{
            fontFamily: 'var(--vm-font-heading)', fontSize: 12,
            letterSpacing: '0.3em', textTransform: 'uppercase',
            color: 'var(--vm-red)', background: 'transparent',
            border: '1px solid var(--vm-red)', padding: '12px 18px', cursor: 'pointer',
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  const slide = slides[Math.max(0, Math.min(slideIndex, total - 1))];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: '#000', zIndex: 60,
        overflow: 'hidden',
      }}
    >
      <div key={slide.id} className="vm-cross-dissolve-enter" style={{ width: '100%', height: '100%' }}>
        <Slide slide={slide} />
      </div>

      {/* Tap zones — explicit 25vw left/right; center 50vw is non-tappable. */}
      <button
        data-testid="tap-zone-prev"
        aria-label="Previous slide"
        onClick={goPrev}
        style={{
          position: 'absolute', top: 0, left: 0, width: '25vw', height: '100%',
          background: 'transparent', border: 'none', cursor: 'pointer',
          zIndex: 4,
        }}
      />
      <button
        data-testid="tap-zone-next"
        aria-label="Next slide"
        onClick={goNext}
        style={{
          position: 'absolute', top: 0, right: 0, width: '25vw', height: '100%',
          background: 'transparent', border: 'none', cursor: 'pointer',
          zIndex: 4,
        }}
      />

      <PaginationTicks total={total} active={slideIndex} />
    </div>
  );
}
